import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircleRounded,
  CancelRounded,
  HelpOutlineRounded,
  HourglassTopRounded,
  GavelRounded,
  GroupsRounded,
  StarRounded,
  EditNoteRounded,
} from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { admissionsApi } from '../../../lib/api/admissions.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { errorText } from '../../../lib/error-text';
import { formatDate } from '../../../lib/i18n-format';

// ─── Types ──────────────────────────────────────────────────────────────────

// Firestore stores null for unreturned votes, 'approve'|'reject'|'clarify' when submitted
type SubmittedVerdict = 'approve' | 'reject' | 'clarify';
type Verdict = SubmittedVerdict | 'pending'; // 'pending' is used only for display

interface ReviewVoteDoc {
  id: string;
  reviewerName: string;
  required: boolean;
  verdict: SubmittedVerdict | null; // null = not yet submitted in Firestore
  notes?: string;
  submittedAt?: { seconds: number } | null;
}

// ─── Hook: live votes from Firestore ────────────────────────────────────────

function useReviewVotes(tenantId: string | null, admissionId: string) {
  const [votes, setVotes] = useState<ReviewVoteDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId || !admissionId) return;
    const q = query(collection(db, 'tenants', tenantId, 'admissions', admissionId, 'reviews'));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ReviewVoteDoc);
      setVotes(docs);
      setLoading(false);
    });
    return unsub;
  }, [tenantId, admissionId]);

  return { votes, loading };
}

// ─── Verdict Badge ───────────────────────────────────────────────────────────

const VERDICT_META: Record<
  Verdict,
  { label: string; color: 'success' | 'error' | 'warning' | 'default'; icon: React.ReactNode }
> = {
  approve: { label: 'Aprobado', color: 'success', icon: <CheckCircleRounded fontSize="small" /> },
  reject: { label: 'Rechazado', color: 'error', icon: <CancelRounded fontSize="small" /> },
  clarify: {
    label: 'Clarificación',
    color: 'warning',
    icon: <HelpOutlineRounded fontSize="small" />,
  },
  pending: { label: 'Pendiente', color: 'default', icon: <HourglassTopRounded fontSize="small" /> },
};

function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const meta = VERDICT_META[verdict] ?? VERDICT_META.pending;
  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      icon={meta.icon as React.ReactElement}
      sx={{ fontWeight: 600, fontSize: '0.72rem' }}
    />
  );
}

// ─── VoteRow ─────────────────────────────────────────────────────────────────

function VoteRow({ vote, isCurrentUser }: { vote: ReviewVoteDoc; isCurrentUser?: boolean }) {
  const initials = vote.reviewerName
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const submittedDate = vote.submittedAt
    ? formatDate(vote.submittedAt.seconds * 1000, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        background: isCurrentUser ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.02)',
        border: isCurrentUser
          ? '1px solid rgba(99,102,241,0.35)'
          : '1px solid rgba(255,255,255,0.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.8rem',
            flexShrink: 0,
            bgcolor: isCurrentUser ? 'primary.main' : 'primary.dark',
          }}
        >
          {initials}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>
              {vote.reviewerName}
            </Typography>
            {isCurrentUser && (
              <Chip
                label="Tú"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700 }}
              />
            )}
            {vote.required && (
              <Tooltip title="Revisor requerido">
                <StarRounded sx={{ fontSize: 14, color: 'warning.main' }} />
              </Tooltip>
            )}
            {submittedDate && (
              <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                · {submittedDate}
              </Typography>
            )}
          </Stack>
        </Box>
        <VerdictBadge verdict={(vote.verdict as Verdict) ?? 'pending'} />
      </Box>
      {vote.notes && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, pl: 6, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
        >
          {vote.notes}
        </Typography>
      )}
    </Box>
  );
}

// ─── Submit Vote Dialog ──────────────────────────────────────────────────────

interface SubmitVoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (verdict: 'approve' | 'reject' | 'clarify', notes: string) => Promise<void>;
  loading: boolean;
  /** When true, shows a rectification notice and changes the dialog title */
  isRectification?: boolean;
}

const VERDICT_OPTIONS: Array<{
  value: 'approve' | 'reject' | 'clarify';
  label: string;
  color: 'success' | 'error' | 'warning';
}> = [
  { value: 'approve', label: 'Aprobar', color: 'success' },
  { value: 'reject', label: 'Rechazar', color: 'error' },
  { value: 'clarify', label: 'Pedir Aclaración', color: 'warning' },
];

function SubmitVoteDialog({
  open,
  onClose,
  onSubmit,
  loading,
  isRectification,
}: SubmitVoteDialogProps) {
  const [verdict, setVerdict] = useState<'approve' | 'reject' | 'clarify' | null>(null);
  const [notes, setNotes] = useState('');

  const handleSubmit = async () => {
    if (!verdict) return;
    await onSubmit(verdict, notes);
    setVerdict(null);
    setNotes('');
  };

  const handleClose = () => {
    setVerdict(null);
    setNotes('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: '1px solid rgba(255,255,255,0.08)' } }}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        {isRectification ? (
          <>
            <EditNoteRounded sx={{ color: 'warning.main' }} /> Rectificar Veredicto
          </>
        ) : (
          <>
            <GavelRounded sx={{ color: 'primary.main' }} /> Emitir Veredicto
          </>
        )}
      </DialogTitle>
      <DialogContent>
        {isRectification && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Tu veredicto anterior quedará registrado en el historial de auditoría. Este cambio es
            trazable.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" mb={2}>
          {isRectification
            ? 'Selecciona tu nuevo veredicto y explica el motivo del cambio.'
            : 'Selecciona tu veredicto y agrega notas o comentarios para el comité.'}
        </Typography>
        <Stack direction="row" gap={1} mb={3} flexWrap="wrap">
          {VERDICT_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={verdict === opt.value ? 'contained' : 'outlined'}
              color={opt.color}
              onClick={() => setVerdict(opt.value)}
              disabled={loading}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              {opt.label}
            </Button>
          ))}
        </Stack>
        <TextField
          label="Notas / comentarios (recomendado)"
          multiline
          minRows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ej. Excelente carta de intención, carta de referencia positiva…"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color={
            verdict
              ? (VERDICT_OPTIONS.find((o) => o.value === verdict)?.color ?? 'primary')
              : 'primary'
          }
          disabled={!verdict || loading}
          onClick={handleSubmit}
          sx={{ borderRadius: 2 }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Confirmar Veredicto'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Review Panel ────────────────────────────────────────────────────────────

interface ReviewPanelProps {
  admissionId: string;
  /** If provided, shows a "Submit Vote" button for this reviewer */
  currentReviewerId?: string | undefined;
}

export function ReviewPanel({ admissionId, currentReviewerId }: ReviewPanelProps) {
  const { currentTenantId } = useTenantStore();
  const { votes, loading } = useReviewVotes(currentTenantId, admissionId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rectifyDialogOpen, setRectifyDialogOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('sis');
  const [quorumResult, setQuorumResult] = useState<string | null>(null);

  const required = votes.filter((v) => v.required);
  const optional = votes.filter((v) => !v.required);
  const approvedRequired = required.filter((v) => v.verdict === 'approve').length;
  const progressPct =
    required.length > 0 ? Math.round((approvedRequired / required.length) * 100) : 0;

  const myVote = currentReviewerId ? votes.find((v) => v.id === currentReviewerId) : undefined;
  // verdict is null in Firestore when not yet submitted — NOT the string 'pending'
  const canVote = !!currentReviewerId && (!myVote || myVote.verdict === null);
  const canRectify = !!currentReviewerId && !!myVote && myVote.verdict !== null;

  const handleSubmitVote = useCallback(
    async (verdict: 'approve' | 'reject' | 'clarify', notes: string) => {
      if (!currentTenantId || !currentReviewerId) return;
      setSubmitLoading(true);
      setError(null);
      try {
        const result = await admissionsApi.submitVote({
          tenantId: currentTenantId,
          admissionId,
          verdict,
          notes,
        });
        setQuorumResult(result.quorumResult);
        setDialogOpen(false);
      } catch (err) {
        setError(errorText(err, 'Error al enviar el veredicto'));
      } finally {
        setSubmitLoading(false);
      }
    },
    [currentTenantId, currentReviewerId, admissionId],
  );

  const handleUpdateVote = useCallback(
    async (verdict: 'approve' | 'reject' | 'clarify', notes: string) => {
      if (!currentTenantId || !currentReviewerId) return;
      setSubmitLoading(true);
      setError(null);
      try {
        const result = await admissionsApi.updateVote({
          tenantId: currentTenantId,
          admissionId,
          verdict,
          notes,
        });
        setQuorumResult(result.quorumResult);
        setRectifyDialogOpen(false);
      } catch (err) {
        setError(errorText(err, 'Error al rectificar el veredicto'));
      } finally {
        setSubmitLoading(false);
      }
    },
    [currentTenantId, currentReviewerId, admissionId],
  );

  if (loading) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <CircularProgress size={20} />
      </Paper>
    );
  }

  if (votes.length === 0) return null;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid rgba(99,102,241,0.2)',
          background:
            'linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(139,92,246,0.03) 100%)',
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <GroupsRounded sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Revisión del Comité
            </Typography>
            <Chip label={`${votes.length} revisores`} size="small" sx={{ fontSize: '0.72rem' }} />
          </Stack>
          <Stack direction="row" gap={1}>
            {canVote && (
              <Button
                variant="contained"
                size="small"
                startIcon={<GavelRounded />}
                onClick={() => setDialogOpen(true)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Emitir Veredicto
              </Button>
            )}
            {canRectify && (
              <Tooltip title="Tu veredicto anterior quedará registrado en el historial de auditoría">
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={<EditNoteRounded />}
                  onClick={() => setRectifyDialogOpen(true)}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  Rectificar Veredicto
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Quorum result alert */}
        {quorumResult && quorumResult !== 'pending' && (
          <Alert
            severity={
              quorumResult === 'accepted'
                ? 'success'
                : quorumResult === 'approved_pending'
                  ? 'warning'
                  : 'error'
            }
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {quorumResult === 'accepted'
              ? t('reviewPanel.quorum.accepted')
              : quorumResult === 'approved_pending'
                ? t('reviewPanel.quorum.approvedPending')
                : t('reviewPanel.quorum.rejected')}
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Progress bar for required reviewers */}
        {required.length > 0 && (
          <Box mb={2.5}>
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                Aprobaciones requeridas
              </Typography>
              <Typography variant="caption" fontWeight={700} color="primary.main">
                {approvedRequired}/{required.length}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': { borderRadius: 4 },
              }}
            />
          </Box>
        )}

        {/* Required reviewers */}
        {required.length > 0 && (
          <Box mb={2}>
            <Typography
              variant="overline"
              color="text.disabled"
              sx={{ letterSpacing: 1, fontSize: '0.65rem' }}
            >
              Revisores Requeridos
            </Typography>
            <Stack gap={1} mt={0.5}>
              {required.map((v) => (
                <VoteRow
                  key={v.id}
                  vote={v}
                  isCurrentUser={!!currentReviewerId && v.id === currentReviewerId}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Optional reviewers */}
        {optional.length > 0 && (
          <>
            {required.length > 0 && <Divider sx={{ my: 1.5 }} />}
            <Box>
              <Typography
                variant="overline"
                color="text.disabled"
                sx={{ letterSpacing: 1, fontSize: '0.65rem' }}
              >
                Revisores Opcionales
              </Typography>
              <Stack gap={1} mt={0.5}>
                {optional.map((v) => (
                  <VoteRow
                    key={v.id}
                    vote={v}
                    isCurrentUser={!!currentReviewerId && v.id === currentReviewerId}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Paper>

      <SubmitVoteDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmitVote}
        loading={submitLoading}
      />

      <SubmitVoteDialog
        open={rectifyDialogOpen}
        onClose={() => setRectifyDialogOpen(false)}
        onSubmit={handleUpdateVote}
        loading={submitLoading}
        isRectification
      />
    </>
  );
}
